"use client";

import { FC } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/components/shadcn/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/app/components/shadcn/input-otp";
import { Button } from "@/app/components/shadcn/button";
import { ArrowLeft } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { usePhoneFormatter } from "@/app/hooks/usePhoneFormatter";
import { useResendCode } from "@/app/hooks/useResendCode";
import { trackGeneric } from "@/app/lib/tracking/interactions";
import CountdownError from "./CountdownError";

interface VerificationFormProps {
  form: UseFormReturn<{ code: string }>;
  phoneNumber: string;
  title?: string;
  description?: string;
  submitButtonText?: string;
  isLoading?: boolean;
  error?: string | null;
  onBack?: () => void;
  onResend: () => Promise<void>;
  onErrorClear?: () => void;
}

const VerificationForm: FC<VerificationFormProps> = ({
  form,
  phoneNumber,
  title = "Doğrulama Kodu",
  description = "Telefon numaranıza gönderilen 5 haneli kodu girin",
  submitButtonText = "Doğrula",
  isLoading,
  error,
  onBack,
  onResend,
  onErrorClear,
}) => {
  const { formatPhoneNumber } = usePhoneFormatter();
  const { countdown, canResend, isResending, handleResend, formatTime } =
    useResendCode({
      onResend,
      initialCountdown: 120,
    });

  return (
    <div className="space-y-6">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          disabled={isLoading}
        >
          <ArrowLeft className="w-4 h-4" />
          Geri dön
        </button>
      )}

      <div className="text-center space-y-3">
        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
        <p className="text-sm font-medium text-gray-900">
          {formatPhoneNumber(phoneNumber)}
        </p>
      </div>

      <FormField
        control={form.control}
        name="code"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium text-gray-700">
              Doğrulama Kodu
            </FormLabel>
            <FormControl>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={5}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isLoading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <CountdownError error={error} onClear={onErrorClear} />

      <Button
        type="submit"
        className="w-full h-12 text-base font-medium"
        disabled={isLoading}
      >
        {isLoading ? "Doğrulanıyor..." : submitButtonText}
      </Button>

      <div className="text-center space-y-2">
        <p className="text-sm text-gray-600">Kod gelmedi mi?</p>
        {canResend ? (
          <button
            type="button"
            onClick={() => { trackGeneric("verification_resend", "click"); handleResend(); }}
            className="text-sm font-medium text-black hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isResending || isLoading}
          >
            {isResending ? "Gönderiliyor..." : "Yeniden gönder"}
          </button>
        ) : (
          <p className="text-sm text-gray-500">
            Yeniden gönderebilirsiniz: {formatTime(countdown)}
          </p>
        )}
      </div>
    </div>
  );
};

export default VerificationForm;